"use client"

import React, { useRef, useState } from "react"
import ReCAPTCHA from "react-google-recaptcha"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { PRODUCT_ACCORDION_DELIVERY_INFO } from "@constants/product-page"

/** Renders the canonical lead-time list (shared verbatim copy, avoids a second hand-maintained duplicate) */
const DeliveryLeadTimes: React.FC = () => (
  <>
    {PRODUCT_ACCORDION_DELIVERY_INFO.map((item, i) => (
      <p className="ctct-body-text" key={i}>
        {item.type === "term" && item.question && <strong>{item.question}: </strong>}
        {item.answer.map((segment, j) =>
          segment.emphasis === "bold" ? (
            <strong key={j}>{segment.text}</strong>
          ) : segment.emphasis === "italic" ? (
            <em key={j}>{segment.text}</em>
          ) : (
            <React.Fragment key={j}>{segment.text}</React.Fragment>
          )
        )}
      </p>
    ))}
  </>
)

/** Shared answer content reused verbatim across multiple sidebar sections (matches reference site's own duplication) */
const UkDeliveriesContent: React.FC = () => (
  <>
    <p className="ctct-body-text">
      Please note that the stated transit times below relate to the shipping time after the order leaves our warehouse and additional processing times may apply to some orders. We process new orders up until 2pm each day, after which point your order is considered as being placed the following day. (In reality, we continue processing after 2pm, but this is our stated cut-off and we cannot guarantee same day processing for orders placed after this point.)
    </p>
    <DeliveryLeadTimes />
  </>
)

const INTERNATIONAL_DELIVERIES_TEXT =
  "We ship worldwide and offer a range of delivery options to suit your needs. We utilise a range of couriers including Royal Mail, PostNL, Hermes, Norsk Global, DPD and Deutsche Poste. We offer tracked and express shipping to all countries. To view the current shipping options to your country, simply select your country at checkout."

const PrintingDetailsContent: React.FC = () => (
  <>
    <p className="ctct-body-text">
      UKSoccershop offers one of the most extensive football shirt printing services that you will find online. Since our formation in 2004, we have handled thousands of different printing styles and printed hundreds of thousands of orders, making us a premier choice for your football shirt printing needs.
    </p>
    <p className="ctct-body-text">
      You can choose your favourite players name, both from the current squad or club legends. For ease of ordering, we endeavour to make the most popular options available as a dropdown menu on the product page, to help you choose your favourite player with the correct squad number.
    </p>
    <p className="ctct-body-text">
      You can also choose to make it personal and add any name and number you want. Whether you are buying for yourself, a friend, or a child, you can add any name or nickname.
    </p>
    <p className="ctct-body-text">
      Where official printing styles are not available, we use fan printing, which is in most cases very similar to the official printing in font, color, texture and size, but without the club crest at the base of the numbers.
    </p>
    <p className="ctct-body-text">All Premier League shirt printing uses official printing.</p>
  </>
)

const TrackOrderContent: React.FC = () => (
  <>
    <p className="ctct-body-text">
      To find out the latest information on your order, you should visit your{" "}
      <LocalizedClientLink href="/account/orders" className="ctct-inline-link">My Account</LocalizedClientLink>{" "}
      page. To do this, please login using the link on the very top left hand corner of the screen. Once you are within your account, click on My Orders to bring up your order history.
    </p>
    <p className="ctct-body-text">
      You can also find out the latest information on your order without login, via the{" "}
      <LocalizedClientLink href="/account/orders" className="ctct-inline-link">Track My Order</LocalizedClientLink>{" "}
      page.
    </p>
    <p className="ctct-body-text">
      We filter our orders through various stages, to help you see the up to date status of your order at any particular point in time. Within your My Orders page you will see the current status of your order.
    </p>
    <p className="ctct-body-text">
      Clicking on the &ldquo;request update&rdquo; button within your order history will provide a more in depth summary of your current order status. It will also provide you with answers to the most frequently asked questions as well as providing you with an option to request further assistance where appropriate.
    </p>
    <p className="ctct-body-text">Within your account, you can also find options to:</p>
    <ul className="ctct-list">
      <li>Change your delivery address</li>
      <li>Notify us of your intention to return an item</li>
      <li>Check on the status of an existing return or refund</li>
      <li>Report a lost or missing parcel</li>
      <li>Cancel/change outstanding items on your order</li>
    </ul>
  </>
)

const CancelOrderContent: React.FC = () => (
  <>
    <p className="ctct-body-text">We&rsquo;re pretty quick at picking and packing your order, therefore we need to be quick to change or cancel your order once your purchase has been confirmed.</p>
    <p className="ctct-body-text">We are unable to accept the cancellation or return of your order once we have started processing your order. After placing your order, you will have a small window before we start processing the order, during which time you will be able to cancel.</p>
    <p className="ctct-body-text">To do so, simply login to your account and select the option to cancel. This will lock the order in our system so it will not be processed.</p>
    <p className="ctct-body-text">If your order status has been changed to Being Picked for Dispatch, it is usually too late for us to make changes to the order as this means your order has been packed and ready for shipping.</p>
    <p className="ctct-body-text">For printed shirts, once we have begun processing the order, we are unable to accept cancellation because we start building the order immediately. Please note, our order statuses are updated in batch and not in real-time, so your shirt is often printed long before the status updates.</p>
    <p className="ctct-body-text">You are welcome to email us and we will change/cancel if we can, but we cannot guarantee it once processing has begun.</p>
  </>
)

const WrongOrderContent: React.FC = () => (
  <>
    <p className="ctct-body-text">
      In the unlikely event we send you the wrong product, we will refund the cost of return postage back to the card which was used to purchase the item(s). Please send the product using the instructions above by recorded delivery and enclose a completed returns slip (this can be downloaded from inside your account) and proof of postage. As soon as we receive the returned item(s) we will despatch the correct product and issue you with the postage refund.
    </p>
    <p className="ctct-body-text">
      If you are returning a high-value item, we recommend using a special/guaranteed delivery service &mdash; UKSoccershop will not take responsibility for lost returns above this value which are not sent via a trackable, insured service. For lower-value returns, please use a recorded delivery service; should the parcel be lost, we will cover the difference between the order value and the value paid out by the postal carrier.
    </p>
  </>
)

const RefundStatusContent: React.FC = () => (
  <>
    <p className="ctct-body-text">When we process your refund, you will get an email confirming that the refund has been initiated. Please allow 5 working days for this to reach your account (although in practice this is often quicker).</p>
    <p className="ctct-body-text">If you have not received your refund after 5 working days, please contact our Customer Service team with your order details and they will escalate with our payment processors.</p>
    <p className="ctct-body-text">If you have received your refund, but you feel the amount is incorrect, please contact our Customer Service team immediately and they will investigate the issue for you.</p>
  </>
)

type SectionId =
  | "faq" | "trackOrder" | "placeOrder" | "shipping" | "returns"
  | "personalisation" | "orderQueries" | "storeInfo" | "corporate"
  | "technical" | "privacy" | "cookie" | "terms" | "contact"

const IconFa = ({ d }: { d: string }) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d={d} />
  </svg>
)

const sidebarSections: { id: SectionId; d: string; label: string }[] = [
  { id: "faq",             d: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z",   label: "Top 5 frequently asked questions" },
  { id: "trackOrder",      d: "M21 16.5c0 .38-.21.71-.53.88l-7.9 4.44c-.16.09-.34.13-.53.13s-.37-.04-.53-.13l-7.9-4.44A1.001 1.001 0 0 1 3 16.5v-9c0-.38.21-.71.53-.88l7.9-4.44a1.01 1.01 0 0 1 1.06 0l7.9 4.44c.32.17.53.5.53.88v9zM12 4.15L6.04 7.5 12 10.85l5.96-3.35L12 4.15zM5 15.91l6 3.38v-6.71L5 9.21v6.7zm14 0v-6.7l-6 3.38v6.71l6-3.39z", label: "WHERE IS MY ORDER?" },
  { id: "placeOrder",      d: "M20 6h-2.18c.07-.44.18-.88.18-1.36C18 2.05 15.96 0 13.36 0c-1.04 0-2.02.38-2.76 1.05L12 2.5l-1.6-1.45C9.66.38 8.68 0 7.64 0 5.04 0 3 2.05 3 4.64c0 .48.11.92.18 1.36H1c-1.1 0-2 .9-2 2v11c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-7.82-4.26c.37-.35.86-.56 1.18-.56 1.33 0 2.14.91 2.14 2.46 0 .79-.21 1.36-.55 1.8H12V2.77l.18-.03zM5 4.64C5 3.09 5.81 2.18 7.14 2.18c.32 0 .81.21 1.18.56L8.5 3l.18.03V6H7.41C7.07 5.56 5 4.93 5 4.64zM11 19H3V9h8v10zm8 0h-8V9h8v10z", label: "Placing Your Order" },
  { id: "shipping",        d: "M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z", label: "Delivery & Shipping" },
  { id: "returns",         d: "M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z", label: "Return & Exchange Policy" },
  { id: "personalisation", d: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z", label: "Personalisation" },
  { id: "orderQueries",    d: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z", label: "Order Queries" },
  { id: "storeInfo",       d: "M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z", label: "Store Information" },
  { id: "corporate",       d: "M20 6H4c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 12H4V8h16v10zM4 2h16v2H4z", label: "Corporate Enquiries" },
  { id: "technical",       d: "M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z", label: "Technical Info" },
  { id: "privacy",         d: "M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z", label: "Privacy Policy" },
  { id: "cookie",          d: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z", label: "Cookie Policy" },
  { id: "terms",           d: "M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zM6 20V4h7v5h5v11H6z", label: "Terms and Conditions" },
  { id: "contact",         d: "M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z", label: "Contact Us" },
]

const openingHours = [
  { day: "Monday",    hours: "8.30am - 5.00pm" },
  { day: "Tuesday",   hours: "8.30am - 5.00pm" },
  { day: "Wednesday", hours: "8.30am - 5.00pm" },
  { day: "Thursday",  hours: "8.30am - 5.00pm" },
  { day: "Friday",    hours: "8.30am - 5.00pm" },
  { day: "Saturday",  hours: "8.30am - 4.00pm" },
]

const subjectOptions = [
  "Please Select",
  "PLACING YOUR ORDER",
  "DELIVERY & SHIPPING",
  "RETURN INFORMATION",
  "PERSONALISATION",
  "ORDER QUERIES",
  "STORE INFORMATION",
  "COMPLAINTS/QUERY",
]

type AccordionItem = { id: string; q: string; a: React.ReactNode }

const faqItems: AccordionItem[] = [
  {
    id: "faq-new-1",
    group: "New Orders",
    subjects: ["ALL", "DELIVERY & SHIPPING"],
    q: "UK Deliveries",
    a: <UkDeliveriesContent />,
  } as any,
  {
    id: "faq-new-2",
    group: "New Orders",
    subjects: ["ALL", "DELIVERY & SHIPPING"],
    q: "International Deliveries",
    a: INTERNATIONAL_DELIVERIES_TEXT,
  } as any,
  {
    id: "faq-new-3",
    group: "New Orders",
    subjects: ["ALL", "PLACING YOUR ORDER"],
    q: "What Payment Methods Do You Accept?",
    a: "We currently accept online payments with a number of credit/debit cards and also PayPal. The current credit cards we accept include: Visa, Mastercard, American Express, Maestro. Credit and debit card payments are processed securely via www.stripe.com, a leading global payment processor. Alternatively, you can choose to pay via your PayPal account.",
  } as any,
  {
    id: "faq-new-4",
    group: "New Orders",
    subjects: ["ALL", "PERSONALISATION"],
    q: "Printing Details",
    a: <PrintingDetailsContent />,
  } as any,
  {
    id: "faq-existing-1",
    group: "Existing Orders",
    subjects: ["ALL", "ORDER QUERIES"],
    q: "How Do I Track My Order?",
    a: <TrackOrderContent />,
  } as any,
  {
    id: "faq-existing-2",
    group: "Existing Orders",
    subjects: ["ALL", "RETURN INFORMATION"],
    q: "Returns Policy",
    a: (
      <>
        <p className="ctct-body-text">We hope that you will be happy with your purchase, but should you wish to return or exchange an item, please follow the instructions below.</p>
        <p className="ctct-body-text">Please note we are unable to accept the return of printed shirts or personalised items, unless the item is deemed to be faulty and the fault is that of UKSoccershop. The same applies if a player leaves the club or changes his squad number during his time at the club.</p>
        <p className="ctct-body-text">
          Please inform us, either by email or by phone, of any item you intend to return within 14 days of taking delivery. Returns must be made within 28 days of taking delivery. All items must be returned with all outer packaging and labels intact and be in the exact condition in which they were received. Please include a copy of your return form inside the package to allow us to identify the order as yours &mdash; you would be amazed how many people send us a shirt and absolutely nothing to help us work out whose order it is. The return form can be accessed through your account. Please also note on the invoice the reason for return and whether you are requesting a refund or an exchange.
        </p>
        <p className="ctct-body-text">
          Returns will be inspected upon receipt. If a returned item is found to be non-faulty, correct, and of the correct size, we will not accept any costs incurred in returning the item. If an exchange item is to be sent, the customer will be responsible for the shipping cost on the exchange item as determined by value in our shipping rate chart. In the event that an exchange is requested but not available a refund will be issued.
        </p>
        <p className="ctct-body-text">Refunds are issued to the credit card used to make the purchase. Returns on orders paid for with a check or money order are returnable for store credit or exchange only in accordance with the exchange policy above.</p>
        <p className="ctct-body-text">
          Please send returns to this address: UKSS, 18 Kilbirnie Place, Glasgow, G5 8QR, UNITED KINGDOM. We strongly recommend that you send any returns via an insured, trackable service. Returned items remain the property of the customer until they are safely delivered at the address listed above. We will not be responsible for items lost or damaged during return shipment.
        </p>
      </>
    ),
  } as any,
  {
    id: "faq-existing-3",
    group: "Existing Orders",
    subjects: ["ALL", "ORDER QUERIES"],
    q: "Can I Cancel My Order?",
    a: <CancelOrderContent />,
  } as any,
  {
    id: "faq-existing-4",
    group: "Existing Orders",
    subjects: ["ALL", "ORDER QUERIES"],
    q: "I Have Received The Wrong Order",
    a: <WrongOrderContent />,
  } as any,
  {
    id: "faq-existing-5",
    group: "Existing Orders",
    subjects: ["ALL", "ORDER QUERIES"],
    q: "I want to enquire about the status of my refund",
    a: <RefundStatusContent />,
  } as any,
]

const newOrderFaqItems = faqItems.filter((item: any) => item.group === "New Orders")
const existingOrderFaqItems = faqItems.filter((item: any) => item.group === "Existing Orders")

const Accordion: React.FC<{
  items: AccordionItem[]
  openItems: Set<string>
  onToggle: (id: string) => void
}> = ({ items, openItems, onToggle }) => (
  <div className="ctct-faq-list">
    {items.map((item) => (
      <div key={item.id} className="ctct-faq-item">
        <h4
          className="ctct-faq-q"
          onClick={() => onToggle(item.id)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && onToggle(item.id)}
          aria-expanded={openItems.has(item.id)}
        >
          {item.q}
          <span className="ctct-faq-plus">{openItems.has(item.id) ? "−" : "+"}</span>
        </h4>
        {openItems.has(item.id) && (
          <div className="ctct-faq-answer">
            <div className="ctct-body-text">{item.a}</div>
          </div>
        )}
      </div>
    ))}
  </div>
)

const HoursTable: React.FC = () => (
  <table className="ctct-hours-table">
    <tbody>
      {openingHours.map((row, i) => (
        <tr key={row.day}>
          <td className={`ctct-hours-day ctct-hours-cell${i % 2 === 0 ? " ctct-row-light" : " ctct-row-mid"}`}>{row.day}</td>
          <td className={`ctct-hours-time ctct-hours-cell${i % 2 === 0 ? " ctct-row-light" : " ctct-row-mid"}`}>{row.hours}</td>
        </tr>
      ))}
    </tbody>
  </table>
)

const ContactTemplate: React.FC<{ initialSection?: SectionId }> = ({
  initialSection = "contact",
}) => {
  const [activeSection, setActiveSection] = useState<SectionId>(initialSection)
  const [openItems, setOpenItems] = useState<Set<string>>(new Set())
  const [selectedSubject, setSelectedSubject] = useState("Please Select")

  const [orderNo, setOrderNo] = useState("")
  const [contactName, setContactName] = useState("")
  const [contactEmail, setContactEmail] = useState("")
  const [contactPhone, setContactPhone] = useState("")
  const [contactMessage, setContactMessage] = useState("")
  const [contactRecaptchaToken, setContactRecaptchaToken] = useState<string | null>(null)
  const [contactError, setContactError] = useState<string | null>(null)
  const [contactSubmitted, setContactSubmitted] = useState(false)
  const contactRecaptchaRef = useRef<ReCAPTCHA>(null)

  const handleContactSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!contactRecaptchaToken) {
      setContactError("Please fill up the google captcha.")
      return
    }
    setContactSubmitted(true)
    setContactError(null)
    setOrderNo("")
    setContactName("")
    setContactEmail("")
    setContactPhone("")
    setContactMessage("")
    setContactRecaptchaToken(null)
    contactRecaptchaRef.current?.reset()
  }

  const toggleItem = (id: string) => {
    setOpenItems((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const trackOrderItems: AccordionItem[] = [
    {
      id: "to-1",
      q: "How Can I Track My Order?",
      a: <TrackOrderContent />,
    },
  ]

  const placeOrderItems: AccordionItem[] = [
    {
      id: "po-1",
      q: "What Payment Methods Do You Accept?",
      a: "We currently accept online payments with a number of credit/debit cards and also PayPal. The current credit cards we accept include: Visa, Mastercard, American Express, Maestro. Credit and debit card payments are processed securely via www.stripe.com, a leading global payment processor. Alternatively, you can choose to pay via your PayPal account.",
    },
    {
      id: "po-2",
      q: "I'm having a problem placing my order",
      a: "We are sorry to hear that. Please contact our sales team and we will be happy to assist you with placing your order. To help us re-create the issue at your end and troubleshoot, please provide as much information as possible. Specifically, it would be useful to know which browser and version you are using, as well as the page the error is occurring on and any specific error message you are getting. Please note that if you are trying to place an order relating to a promotional offer or voucher code which is close to expiring, we will honour the terms of this offer even after it has expired, assuming you make contact with us regarding the problem prior to the expiry of the offer.",
    },
    {
      id: "po-3",
      q: "Can I send items to someone as a gift?",
      a: "If you want to send something to someone as a gift then you can use their address as the delivery address when you place your order. We offer a gift wrapping service if you would like to make the delivery extra special. We do not send any invoice or paperwork with our orders and do not display the prices, so you can send the items confidentially and the recipient will not know the price of the gift.",
    },
    {
      id: "po-4",
      q: "What Currencies Can I Pay In?",
      a: "We allow you to pay in most major currencies, including GBP, EUR, USD, AUD, CAD, AED, JPY and others. Click the appropriate currency symbol at the top right of the page. When you click one of these symbols, the page you are on will refresh and all prices will be displayed in your new currency. Prices will be displayed in your new currency until you change it again, even if you leave the site and come back later.",
    },
    {
      id: "po-5",
      q: "Are My Payment Details Secure?",
      a: "Please note that the order process of ordering at UKSoccershop.com is via a secure connection. This means that all the personal information you enter will be encrypted so it cannot be read as it travels over the Internet. All your payment card details are encrypted and are never seen by our staff.",
    },
    {
      id: "po-6",
      q: "Where can I find my credit/debit card security number?",
      a: "Your credit/debit card security number is a 3 digit number which can be found on the far right side of the signature strip on the back of your card. This 3 digit number is unique to your card, and gives you extra protection against fraud. If you are paying by American Express, it is the four digit number on the right hand side of the front of your card.",
    },
    {
      id: "po-7",
      q: "Do I have to Pay Customs Duties?",
      a: "There are no customs duties to pay within the European Union. For international orders, any customs or import duties are levied once the package reaches its destination country. Additional charges for customs clearance must be borne by the recipient. We have no control over these charges and cannot predict what they may be. Customs policies vary widely from country to country and we advise you to contact your local customs office for further information.",
    },
    {
      id: "po-8",
      q: "Size Guides",
      a: "We stock many different brands on our website and each kit manufacturer has different sizing. Where we have the sizing available, we provide additional details in the dropdown menu where you select your size. Once you have selected your size, you will see a 'What is my size?' option. This is a 3rd party tool which aggregates data from many leading retailers throughout the world, and incorporates customer feedback to help build accurate data on sizing and advise you on the best fit. Our internal data shows a significantly lower % of returns from customers who use this tool as opposed to those who do not. We therefore recommend using this tool to help guide your purchase.",
    },
  ]

  const shippingItems: AccordionItem[] = [
    {
      id: "sh-1",
      q: "UK Deliveries",
      a: <UkDeliveriesContent />,
    },
    {
      id: "sh-2",
      q: "Next Day Delivery",
      a: (
        <>
          <p className="ctct-body-text">
            We do operate a next day delivery service - for orders placed before 2pm - on certain types of goods. Please note the delivery options at checkout relate to the shipment method that will be used at the point of dispatch and do not include processing times.
          </p>
          <p className="ctct-body-text">
            We are unable to guarantee next day delivery on printed shirts, t-shirts and personalised items (although we will endeavour to achieve this where possible).
          </p>
          <p className="ctct-body-text">
            Next Working Day Delivery is available on items marked as &lsquo;For Immediate Dispatch&rsquo; on the product page.
          </p>
          <p className="ctct-body-text">Next-Day Delivery will be unavailable during national holidays.</p>
        </>
      ),
    },
    {
      id: "sh-3",
      q: "International Deliveries",
      a: INTERNATIONAL_DELIVERIES_TEXT,
    },
    {
      id: "sh-4",
      q: "Can I request an urgent delivery?",
      a: (
        <>
          <p className="ctct-body-text">
            We treat all orders as urgent, but if you have a specific request or deadline that you need to meet, we can look to give your order that little extra priority. Please note though that some aspects of the order process (such as delivery of printing or stock from external suppliers) are out with our control and we are unable to speed some parts of the order up in these circumstances.
          </p>
          <p className="ctct-body-text">
            All requests for urgent delivery must be made prior to the order being processed and we are unable to take retrospective requests for urgent deliveries.
          </p>
          <p className="ctct-body-text">
            If your order is conditional on a certain delivery date being met, you should contact our sales team prior to placing the order. Our team will check on availability and advise you of likely delivery terms so you can place the order, which will then be marked as a priority.
          </p>
          <p className="ctct-body-text">
            If you wish to order and have a certain delivery date in mind which is non-essential but you would hope we can meet, the quickest way forward is to go ahead and place your order via the website. We have different teams who process the orders and who answer our emails, so this will ensure your order goes immediately into our system and will let it get processed in the quickest time.
          </p>
          <p className="ctct-body-text">
            Of course, all orders are considered urgent by UKSoccershop and we ship everything as quickly as we can, so there is a good chance your delivery will be made on time even where an urgent request has not been made.
          </p>
        </>
      ),
    },
    {
      id: "sh-5",
      q: "Stock Availability",
      a: <UkDeliveriesContent />,
    },
    {
      id: "sh-6",
      q: "Customs & Duty",
      a: (
        <>
          <p className="ctct-body-text">
            Unless otherwise stated, all items are shipped from the United Kingdom and may incur duty &amp; other charges upon entering other countries. Any duty, clearance, tax or carrier charges must be borne by the recipient and we will not accept liability for any such costs.
          </p>
          <p className="ctct-body-text">
            We are unable to advise what the thresholds and rates are for other countries as these are determined by the respective governments and can vary significantly between countries and even items. If upon delivery there are any additional charges due our carrier will contact you to arrange payment.
          </p>
        </>
      ),
    },
  ]

  const personalisationItems: AccordionItem[] = [
    {
      id: "pe-1",
      q: "Printing Details",
      a: <PrintingDetailsContent />,
    },
    {
      id: "pe-2",
      q: "Delivery Times",
      a: (
        <>
          <p className="ctct-body-text">
            We process new orders up until 2pm each day, after which point your order is considered as being placed the following day. (In reality, we continue processing after 2pm, but this is our stated cut-off and we cannot guarantee same day processing for orders placed after this point.) The following types of orders have the additional processing lead-times. Please note that in many cases, we dispatch faster than this, but would rather quote longer lead-times and deliver faster than you expect than vice versa.
          </p>
          <p className="ctct-body-text">
            <strong>Printed Shirts</strong> - On average these are shipped within 2-5 business days. Depending on order volumes, next day or even same day shipments are often possible, but at peak times, these can take around 7-10 business days on average.
          </p>
          <p className="ctct-body-text">
            <strong>Other Personalised Products</strong> - On average these are shipped within 2-5 business days. Depending on order volumes, next day or even same day shipments are often possible, but at peak times, these can take around 7-10 business days.
          </p>
          <p className="ctct-body-text">
            <strong>T-Shirts</strong> - On average these are shipped within 2-5 business days. Depending on order volumes, next day or even same day shipments are often possible, but at peak times, these can take around 7-10 business days.
          </p>
        </>
      ),
    },
    {
      id: "pe-3",
      q: "Cancellation & Returns",
      a: (
        <>
          <p className="ctct-body-text">
            We are unable to accept the cancellation or return of your order once we have started processing your order. After placing your order, you will have a small window before we start processing the order, during which time you will be able to cancel. To do so, simply login to your account and select the option to cancel. This will lock the order in our system so it will not be processed. Once we have begun processing the order, we are unable to accept cancellation because we start building the order immediately. Please note, our order statuses are updated in batch and not in real-time, so your shirt is often printed long before the status updates.
          </p>
          <p className="ctct-body-text">
            We are unable to accept the return of printed or personalised items, unless the item is faulty. Once a name or number has been printed on a shirt we will not offer a refund or exchange unless the shirt is faulty. The same applies if a player leaves the club or changes his squad number during his time at the club.
          </p>
        </>
      ),
    },
  ]

  const orderQueryItems: AccordionItem[] = [
    {
      id: "oq-1",
      q: "How Do I Track My Order?",
      a: <TrackOrderContent />,
    },
    {
      id: "oq-2",
      q: "Can I Cancel My Order?",
      a: <CancelOrderContent />,
    },
    {
      id: "oq-3",
      q: "I Have Received The Wrong Order",
      a: <WrongOrderContent />,
    },
    {
      id: "oq-4",
      q: "My order is incomplete or item(s) are missing",
      a: "At certain times, due to unforeseen stock discrepancies, we are unable to send all of the items that you have ordered. If this is the case, we will send you an email letting you know of any items that are missing from your order. Unless otherwise stated, in the absence of alternate instruction from you, we will endeavour to ship the balance of your order as quickly as possible. Please note, if you wish to cancel the remaining parts of your order, or change them for alternate products, you can do this at any time. Simply login to your account, select your order and follow the instructions to make any changes that you wish.",
    },
    {
      id: "oq-5",
      q: "I have a faulty/damaged item",
      a: "If an item arrives damaged or faulty then please contact our Customer Care team, who will discuss your return options. When contacting us, please include pictures to demonstrate the fault. We will then send the item to our inspection team who will assess whether a manufacturing fault is present, then depending on the results offer a refund or exchange. Please note, we may require the product back to do a physical inspection before making a final judgement on how to proceed.",
    },
    {
      id: "oq-6",
      q: "My order has been cancelled but I've still been charged",
      a: "As with the majority of online retailers, once you have entered your payment details your bank will place an authorisation against that full order amount in your account. This will show as a pending amount on your statement but will not actually be taken by us. As soon as we cancel an order we automatically send information to your bank to ask them to release any outstanding authorisation. This process will take up to 7 days depending on your bank's procedures. If the charge is still showing on your account after 7 days then please contact us, because it indicates there may be a problem, and we will investigate directly with our card processors.",
    },
    {
      id: "oq-7",
      q: "I want to enquire about the status of my refund",
      a: <RefundStatusContent />,
    },
  ]

  const technicalItems: AccordionItem[] = [
    {
      id: "tech-1",
      q: "I cannot login into my account",
      a: (
        <>
          <p className="ctct-body-text">
            When you want to shop with us or access your account, we ask you to sign in. If you find that your email address or password is not recognised, please make sure you are using the same email address and password that you used when you registered with us. If you can&rsquo;t remember your password, simply select the &lsquo;I forgot my Password&rsquo; box on the sign-in page, and we will email a reminder of what your password is. You can change your password, main email address or any of your other details at any time just by signing in to{" "}
            <LocalizedClientLink href="/account" className="ctct-inline-link">My Account</LocalizedClientLink>.
          </p>
          <p className="ctct-body-text">
            If you are experiencing any other technical difficulties while using our site please email us. To help us look into your problem please try to include as many of the following details as possible: which browser you are using; what page or category you were trying to access; when the problem occurred; and any error message that appeared on the screen at the time.
          </p>
        </>
      ),
    },
    {
      id: "tech-2",
      q: "I would like to request additional information?",
      a: "If you require further information about any aspect about your order or any service provided by our website, our customer service team are more than happy to assist you. You can submit a ticket which will be reviewed by our customer service team and replied to as soon as possible.",
    },
  ]

  const cookieItems: AccordionItem[] = [
    {
      id: "ck-1",
      q: "What Are Cookies",
      a: (
        <>
          <p className="ctct-body-text">
            As is common practice with almost all professional websites this site uses cookies, which are tiny files that are downloaded to your computer, to improve your experience. This page describes what information they gather, how we use it and why we sometimes need to store these cookies. We will also share how you can prevent these cookies from being stored however this may downgrade or &lsquo;break&rsquo; certain elements of the site&rsquo;s functionality.
          </p>
          <p className="ctct-body-text">For more general information on cookies see the Wikipedia article on HTTP Cookies.</p>
        </>
      ),
    },
    {
      id: "ck-2",
      q: "How We Use Cookies",
      a: "We use cookies for a variety of reasons detailed below. Unfortunately in most cases there are no industry standard options for disabling cookies without completely disabling the functionality and features they add to this site. It is recommended that you leave on all cookies if you are not sure whether you need them or not in case they are used to provide a service that you use.",
    },
    {
      id: "ck-3",
      q: "Disabling Cookies",
      a: "You can prevent the setting of cookies by adjusting the settings on your browser (see your browser Help for how to do this). Be aware that disabling cookies will affect the functionality of this and many other websites that you visit. Disabling cookies will usually result in also disabling certain functionality and features of this site. Therefore it is recommended that you do not disable cookies.",
    },
    {
      id: "ck-4",
      q: "The Cookies We Set",
      a: (
        <>
          <p className="ctct-body-text"><strong>Account related cookies:</strong> If you create an account with us then we will use cookies for the management of the signup process and general administration. These cookies will usually be deleted when you log out however in some cases they may remain afterwards to remember your site preferences when logged out.</p>
          <p className="ctct-body-text"><strong>Login related cookies:</strong> We use cookies when you are logged in so that we can remember this fact. This prevents you from having to log in every single time you visit a new page. These cookies are typically removed or cleared when you log out to ensure that you can only access restricted features and areas when logged in.</p>
          <p className="ctct-body-text"><strong>Email newsletters related cookies:</strong> This site offers newsletter or email subscription services and cookies may be used to remember if you are already registered and whether to show certain notifications which might only be valid to subscribed/unsubscribed users.</p>
          <p className="ctct-body-text"><strong>Orders processing related cookies:</strong> This site offers e-commerce or payment facilities and some cookies are essential to ensure that your order is remembered between pages so that we can process it properly.</p>
          <p className="ctct-body-text"><strong>Surveys related cookies:</strong> From time to time we offer user surveys and questionnaires to provide you with interesting insights, helpful tools, or to understand our user base more accurately. These surveys may use cookies to remember who has already taken part in a survey or to provide you with accurate results after you change pages.</p>
          <p className="ctct-body-text"><strong>Forms related cookies:</strong> When you submit data to through a form such as those found on contact pages or comment forms cookies may be set to remember your user details for future correspondence.</p>
          <p className="ctct-body-text"><strong>Site preferences cookies:</strong> In order to provide you with a great experience on this site we provide the functionality to set your preferences for how this site runs when you use it. In order to remember your preferences we need to set cookies so that this information can be called whenever you interact with a page is affected by your preferences.</p>
        </>
      ),
    },
    {
      id: "ck-5",
      q: "Third Party Cookies",
      a: (
        <>
          <p className="ctct-body-text">In some special cases we also use cookies provided by trusted third parties. The following section details which third party cookies you might encounter through this site.</p>
          <p className="ctct-body-text">This site uses Google Analytics which is one of the most widespread and trusted analytics solution on the web for helping us to understand how you use the site and ways that we can improve your experience. These cookies may track things such as how long you spend on the site and the pages that you visit so we can continue to produce engaging content.</p>
          <p className="ctct-body-text">For more information on Google Analytics cookies, see the official Google Analytics page.</p>
          <p className="ctct-body-text">Third party analytics are used to track and measure usage of this site so that we can continue to produce engaging content. These cookies may track things such as how long you spend on the site or pages you visit which helps us to understand how we can improve the site for you.</p>
          <p className="ctct-body-text">From time to time we test new features and make subtle changes to the way that the site is delivered. When we are still testing new features these cookies may be used to ensure that you receive a consistent experience whilst on the site whilst ensuring we understand which optimisations our users appreciate the most.</p>
          <p className="ctct-body-text">As we sell products it&rsquo;s important for us to understand statistics about how many of the visitors to our site actually make a purchase and as such this is the kind of data that these cookies will track. This is important to you as it means that we can accurately make business predictions that allow us to monitor our advertising and product costs to ensure the best possible price.</p>
          <p className="ctct-body-text">The Google AdSense service we use to serve advertising uses a DoubleClick cookie to serve more relevant ads across the web and limit the number of times that a given ad is shown to you.</p>
          <p className="ctct-body-text">For more information on Google AdSense see the official Google AdSense privacy FAQ.</p>
          <p className="ctct-body-text">We use adverts to offset the costs of running this site and provide funding for further development. The behavioural advertising cookies used by this site are designed to ensure that we provide you with the most relevant adverts where possible by anonymously tracking your interests and presenting similar things that may be of interest.</p>
          <p className="ctct-body-text">Several partners advertise on our behalf and affiliate tracking cookies simply allow us to see if our customers have come to the site through one of our partner sites so that we can credit them appropriately and where applicable allow our affiliate partners to provide any bonus that they may provide you for making a purchase.</p>
          <p className="ctct-body-text">We also use social media buttons and/or plugins on this site that allow you to connect with your social network in various ways. These social media sites will set cookies through our site which may be used to enhance your profile on their site or contribute to the data they hold for various purposes outlined in their respective privacy policies.</p>
          <p className="ctct-subheading">Rakuten Advertising</p>
          <p className="ctct-body-text">
            Rakuten Advertising may collect personal information when you interact with our digital property, including IP addresses, digital identifiers, information about your web browsing and app usage and how you interact with our properties and ads for a variety of purposes, such as personalization of offers or advertisements, analytics about how you engage with websites or ads and other commercial purposes. For more information about the collection, use and sale of your personal data and your rights, please use the below links.
          </p>
          <p className="ctct-body-text">
            Privacy Policy:{" "}
            <a href="https://rakutenadvertising.com/legal-notices/services-privacy-policy/" target="_blank" rel="noopener noreferrer" className="ctct-inline-link">
              https://rakutenadvertising.com/legal-notices/services-privacy-policy/
            </a>
          </p>
          <p className="ctct-body-text">
            Your Rights/Opt Out:{" "}
            <a href="https://rakutenadvertising.com/legal-notices/services-privacy-rights-request-form/" target="_blank" rel="noopener noreferrer" className="ctct-inline-link">
              https://rakutenadvertising.com/legal-notices/services-privacy-rights-request-form/
            </a>
          </p>
        </>
      ),
    },
    {
      id: "ck-6",
      q: "More Information",
      a: (
        <>
          <p className="ctct-body-text">However if you are still looking for more information then you can contact us through one of our preferred contact methods:</p>
          <p className="ctct-body-text">By visiting this link: https://www.uksoccershop.com/support</p>
        </>
      ),
    },
  ]

  const termsItems: AccordionItem[] = [
    {
      id: "tc-1",
      q: "Site Operation and Ownership",
      a: "UKSoccershop is a sole proprietorship, founded in 2004 and run by [Business Owner Name] T/A UKSoccershop, registered in Scotland, United Kingdom, VAT number [VAT Number]. Our registered office is UKSS, 18 Kilbirnie Place, Glasgow, G5 8QR, UNITED KINGDOM.",
    },
    {
      id: "tc-2",
      q: "Registration",
      a: (
        <>
          <p className="ctct-body-text">You will need to register with UKSoccershop to place an order for products through this website. This can be as a &lsquo;guest&rsquo; with no need to create an account.</p>
          <p className="ctct-body-text">In return for allowing you to use this website and benefit from our services, you agree that any information you provide us about yourself will be true, accurate, current and complete and that you will ensure that this information is kept accurate and up to date. If any incorrect information is supplied, any contractual obligation we have is immediately null and void.</p>
        </>
      ),
    },
    {
      id: "tc-3",
      q: "Conditions of Registration",
      a: (
        <>
          <p className="ctct-body-text">
            We do not sell the products for purchase for children. We sell children&rsquo;s products for purchase by adults. You may only purchase from the site if you are at least 18 years old unless you have express permission from your parents or guardian. If you are under 18, you must ask your parents or guardians before you:
          </p>
          <ul className="ctct-list">
            <li>Email the website, or ask us to email you anything</li>
            <li>Send in any information to us</li>
            <li>Buy anything online</li>
          </ul>
          <p className="ctct-body-text">
            By continuing to use this website and any of the services offered, you are confirming that you have received the consent of a parent or legal guardian. PLEASE NOTE that all minors are recommended to discuss these terms and conditions with their parents or guardians before they complete the registration process.
          </p>
        </>
      ),
    },
    {
      id: "tc-4",
      q: "Copyright",
      a: (
        <>
          <p className="ctct-body-text">
            You acknowledge and agree that all content included on this website including but not limited to website design, text, graphics, audio clips, visual clips, logos, button icons and the selection and arrangement thereof shall remain at all times vested in us or our content suppliers and is protected by UK and international copyright laws. You are permitted to use this material only as expressly authorised by the applicable rights holder. Subject to the foregoing, the page headers, custom graphics and button icons are (unless indicated otherwise) service marks, trademarks, and/or trade dress of ours or its owner. Nothing in these Terms and Conditions grants you any legal rights in the website or sub-sites other than as necessary to access the site or sub-sites.
          </p>
          <p className="ctct-body-text">
            All software used on this website is the property of UKSoccershop, and we are protected by UK and international copyright laws. ALL RIGHTS ARE RESERVED. Permission is granted to electronically copy and to print in hard copy portions of this website for the purposes of placing an order with us and using this website as a shopping resource only. Any other use of materials on this website including but not limited to reproduction for purposes other than those noted above, modification, distribution, transmission, broadcasting, republication, downloading or uploading without our prior written permission is strictly prohibited.
          </p>
        </>
      ),
    },
    {
      id: "tc-5",
      q: "Notification of Copyright Infringement",
      a: (
        <>
          <p className="ctct-body-text">
            We will, in appropriate circumstances, terminate the accounts of users who infringe the intellectual property rights of others. If you believe that your work has been used or copied in a way that constitutes copyright infringement, please provide our Legal Department with a notice (&ldquo;Notice&rdquo;) containing the following elements.
          </p>
          <ul className="ctct-list">
            <li>A physical or electronic signature of the person authorised to act on behalf of the owner of the copyright interest that is alleged to have been infringed;</li>
            <li>A description of the copyrighted work or works that you claim have been infringed and which you request to be removed from the site;</li>
            <li>A description of the location of the material that you claim is being infringed;</li>
            <li>Information sufficient to permit us to contact you, such as your physical address, telephone number and e-mail address;</li>
            <li>A statement by you that you have a good faith belief that the use of the material identified in your Notice in the manner complained of is not authorised by the copyright owner, its agent, or the law;</li>
            <li>A statement by you that the information in your Notice is accurate and that you are the copyright owner or are authorised to act on the copyright owner&rsquo;s behalf.</li>
          </ul>
          <p className="ctct-body-text">You can notify us of any claims of copyright infringement at the following address:</p>
          <p className="ctct-body-text">UKSoccershop, 18 Kilbirnie Place, Glasgow, G5 8QR, UNITED KINGDOM</p>
        </>
      ),
    },
    {
      id: "tc-6",
      q: "Content Supplied by Users",
      a: (
        <>
          <p className="ctct-body-text">
            If you send communications or materials to this website by electronic mail or otherwise, concerning any comments, questions, suggestions or the like, all such communications are, and will be treated as, non-confidential and non-proprietary. Thus, you give up any claim that any use of such material infringes any of your rights including without limitation moral rights, proprietary rights or any other right, including the right to approve the way in which we use such material.
          </p>
          <p className="ctct-body-text">
            Any material submitted to this website, may be adapted, broadcast, changed, copied, disclosed, licensed, performed, posted, published, sold, transmitted or used on a royalty free basis by us anywhere in the world, in any medium, in perpetuity.
          </p>
          <p className="ctct-body-text">
            Please note that this provision does not relate to any personal data that you submit which will be dealt with in accordance with the provisions of this website&rsquo;s &lsquo;Privacy Policy&rsquo; which is part of these Terms and Conditions.
          </p>
        </>
      ),
    },
    {
      id: "tc-7",
      q: "Amending The Terms and Conditions Of Use",
      a: "We may add to, change or remove any part of these Terms and Conditions at any time. Material changes to our Terms and Conditions will be published on the home page of our website, notifying users that such changes have been made. Continued use of the website after the publication of such a notice confirms your agreement to be bound by the variations made by us. It is your responsibility to check these Terms and Conditions from time to time to verify such amendments. We may add, change, discontinue, remove or suspend any other content displayed on this website, including features and specifications of products and services described or depicted on the website, temporarily or permanently, at any time, without notice and without liability.",
    },
    {
      id: "tc-8",
      q: "Privacy",
      a: "UKSoccershop treat customer privacy extremely seriously. Our Privacy Policy provides details about the type of information we may collect from you and how we may use this information. You are asked to read our Privacy Policy, which forms a part of these Terms and Conditions and sets out the way in which your personal data is handled by us.",
    },
    {
      id: "tc-9",
      q: "Site Usage",
      a: "You agree that you will only use our website in a way which is consistent with the Terms and Conditions and which complies with applicable laws and regulations. In particular you agree that you will not use this website to upload or send any material which contains software viruses or other codes, files or programmes designed to interrupt, destroy or limit the functionality of any computer software, hardware or telecommunications equipment or in any other manner which would interfere with or disrupt this website. You acknowledge that this website and any goods that you obtain from our website are provided for your personal use only and may not be used for any commercial purposes or distributed commercially without our permission. You agree that you are solely responsible for keeping your password and other account details confidential.",
    },
    {
      id: "tc-10",
      q: "Purchasing Goods",
      a: (
        <>
          <p className="ctct-body-text">
            Our Help section provides full details on how to order and pay for products. It also addresses any queries you may have about delivery of your products, your right to cancel orders and/or return faulty products and our quality assurance.
          </p>
          <p className="ctct-body-text">We reserve the right to decline orders for bulk or high value purchases and to change price and availability information without notice.</p>
        </>
      ),
    },
    {
      id: "tc-11",
      q: "Multiple Promotions And Discount Codes",
      a: "Please note that any promotions/offers on the website cannot be used in conjunction with any other discounts. Only one discount code is applicable per order. We are not responsible for offers listed on third party websites and cannot honour offers that were not expressly authorised by UKSoccershop.",
    },
    {
      id: "tc-12",
      q: "Product Orders",
      a: "All orders placed through the Website are subject to acceptance by UKSoccershop. We reserve the right to refuse to accept or cancel any order, whether or not the order has been confirmed, for any or no reason, and without liability to you or anyone else. If your credit card has already been charged for an order that is later cancelled, UKSoccershop will issue you a refund.",
    },
    {
      id: "tc-13",
      q: "International Orders",
      a: (
        <>
          <p className="ctct-body-text">If goods are being delivered outside the EU, any customs or import duties levied by the government of the destination country must be borne by the recipient.</p>
          <p className="ctct-body-text">
            Although in many cases the order value will be under the threshold and no duties will be applicable, UKSoccershop has no control over these charges and cannot predict what they may be. Customs policy vary wildly from country to country and are subject to change. We would recommend you contact your local customs office for information prior to placing any order.
          </p>
          <p className="ctct-body-text">
            UKSoccershop accept no liability for these charges. By placing an order you acknowledge the recipient accepts liability for any charges applied by their government (including, but not limited to taxes, duties and holding fees).
          </p>
          <p className="ctct-body-text">
            Furthermore, UKSoccershop will not be liable for any additional costs incurred as a result of customers refusing to pay the applicable customs charges and will charge accordingly for any charges we incur as a result of the recipient refusing to pay the applicable charge levied at Customs.
          </p>
        </>
      ),
    },
    {
      id: "tc-14",
      q: "Availability",
      a: (
        <>
          <p className="ctct-body-text">UKSoccershop will endeavour to display availability as accurately as possible.</p>
          <p className="ctct-body-text">
            In the event we are unable to supply the goods requested we will do our best to find you a suitable alternative that you are happy with, however we will not be bound to offer alternatives of a greater value to the original item, and you may be required to pay a difference.
          </p>
          <p className="ctct-body-text">If you do not wish for an alternative product we will cancel your order, however please be aware that funds may take a few days to reach your account.</p>
          <p className="ctct-body-text">
            In the event of a shortfall of stock received from suppliers for pre-order items, we will fulfil orders in chronological order and will contact any customers who have missed out to discuss alternatives.
          </p>
          <p className="ctct-body-text">
            Pre-Order dates are provisional and although the majority of stock is received on time, these are often beyond our control and we cannot be held responsible for any costs incurred for your time, or services you&rsquo;ve paid for, if there are delays or non supply of product.
          </p>
        </>
      ),
    },
    {
      id: "tc-15",
      q: "Cancellation",
      a: (
        <>
          <p className="ctct-body-text">In accordance with the Consumer Contracts Regulations 2013 you have 14 consecutive days from the day after you receive your parcel to cancel your contract with us.</p>
          <p className="ctct-body-text">Should you wish to do this we require written notification via email or post. You may do this either before you receive your parcel or it can be placed within the parcel upon its return.</p>
          <p className="ctct-body-text">Please fill out our return form and include it within the parcel, otherwise we may not be able to identify the return as yours.</p>
          <p className="ctct-body-text">
            If goods have already been dispatched to you, you will be expected to cover the costs of the return, or pay for the cost of the retrieval if the courier hasn&rsquo;t delivered yet. Please note, retrieval will not always be possible but we will endeavour to do so where possible.
          </p>
          <p className="ctct-body-text">
            We regret that we are unable to cancel orders for personalised items - including all form of printed shirts, both personal and player names - once the personalisation process has begun.
          </p>
          <p className="ctct-body-text">
            Any non-personalised cancelled item must be returned to us in a resalable condition, unused and complete with tags and original packaging. We are unable to accept returns of product which does not comply with this requirement. When returning, please protect product packaging by using additional outer packaging and avoid placing postage labels or tape on the product packaging.
          </p>
          <p className="ctct-body-text">You are responsible for ensuring the safe return to us of the product in a resalable condition.</p>
        </>
      ),
    },
    {
      id: "tc-16",
      q: "Personalised Items",
      a: (
        <>
          <p className="ctct-body-text">
            Personalised items refer to any product which is customised - either with your personal name or of any player, past of present. It also includes any customisation such as initials or squad numbers.
          </p>
          <p className="ctct-body-text">UKSoccershop regret that once an order containing personalised items have been picked, it cannot be changed or cancelled.</p>
          <p className="ctct-body-text">All personalised items cannot be returned unless deemed to have a genuine fault of manufacturer.</p>
          <p className="ctct-body-text">
            UKSoccershop reserve the right to utilise our knowledge and expertise and positioning of the personalisation on the product. Examples shown on the website are for illustrative purposes only and may vary depending on the specific item. Please contact our team before ordering if you require further information about the finished look.
          </p>
          <p className="ctct-body-text">
            Our experienced team will also choose the most appropriate colour for the personalisation. However, we will accept requests for a specific colour, but you should contact us prior to placing the order to request this as we may be unable to change once the order has been processed through our system.
          </p>
        </>
      ),
    },
    {
      id: "tc-17",
      q: "Parcels Lost in Transit",
      a: (
        <>
          <p className="ctct-body-text">
            UKSoccershop are responsible for ensuring that goods in transit to you are at least attempted for delivery and in the case you are unavailable to receive the parcel our customers service team will help you locate and collect the order from the relevant delivery agent.
          </p>
          <p className="ctct-body-text">In the event that you have not received your parcel within the specified delivery time shown in the basket, please contact our Customer Service Team who will assist you.</p>
          <p className="ctct-body-text">We ask that we are given sufficient time to investigate with our delivery agents before either replacing or refunding goods. Investigation time is often longer for parcels sent outside Europe.</p>
          <p className="ctct-body-text">We will not keep you waiting longer than necessary but we find that the majority of parcels are located after investigation and this is a faster solution than sending a replacement.</p>
          <p className="ctct-body-text">
            If delivery is being made to a commercial address (work/hotel), responsibility for locating the parcel rests with the customer as soon as we confirm the parcel has been delivered to the reception area of the requested building. UKSoccershop cannot accept liability for parcels lost or misplaced after delivery is confirmed.
          </p>
        </>
      ),
    },
    {
      id: "tc-18",
      q: "Returns",
      a: (
        <>
          <p className="ctct-body-text">
            All goods supplied by UKSoccershop are covered by the normal manufacturer warranty. If an exchange or refund is required then the items must be returned to us, in their original condition and packaging, within 28 days of your purchase (extensions can be arrange for Christmas and gift presents - contact our customer service team prior to placing your order to arrange).
          </p>
          <p className="ctct-body-text">Unfortunately, we are unable to accept the return of personalised items (including player name shirts) unless it is the fault of the manufacturer. This does not effect your statutory rights.</p>
          <p className="ctct-body-text">We are unable to accept the return of items which are not in the original condition, including all tags and original packaging. If any item shows a sign of wear or is not in a resalable condition they will not be accepted for return.</p>
          <p className="ctct-body-text">We reserve the right to refuse to refund or exchange goods which are deemed to be damaged or tampered with not in accordance with normal use. This does not effect your statutory rights.</p>
          <p className="ctct-body-text">Please address return parcels to: UKSS, 18 Kilbirnie Place, Glasgow, G5 8QR, UNITED KINGDOM</p>
        </>
      ),
    },
    {
      id: "tc-19",
      q: "Links",
      a: "We may provide links to websites owned by us and associated companies from time to time and/or websites that are owned by third parties that are not connected with us including other websites owned by us. All links are provided for your convenience only. Access to websites that are owned by third parties is at your own risk and we do not have any responsibility or liability for such third party websites. Your use of a third party site may be governed by the terms and conditions of that third party site.",
    },
    {
      id: "tc-20",
      q: "Limitation of Liability",
      a: "To the fullest extent permitted by applicable laws, neither we nor any of our respective owners, employees, affiliates or other representatives will be liable for loss or damages arising out of or in connection with the use of, or inability to use, the materials in and/or facilities or services offered through this website, including, but not limited to, indirect or consequential loss or damages, loss of data, income, profit or opportunity, loss of or damage to property and claims of third parties, even if we have been advised of the possibility of such loss or damages or such loss or damages were reasonably foreseeable. If this clause is unenforceable in whole or in part in any jurisdiction due to relevant laws, then in no event shall our total liability to you for all damages, losses, and claims (whether in contract, tort (including, but not limited to, negligence), or otherwise) exceed the amount paid by you, if any, for accessing this website.",
    },
    {
      id: "tc-21",
      q: "Complaints and Comments",
      a: (
        <>
          If you have any complaints or comments about our website or any of the products supplied to you, please contact us by calling our Customer Service Team or emailing{" "}
          <a href="mailto:info@uksoccershop.com" className="ctct-inline-link">info@uksoccershop.com</a>{" "}
          or through the Contact Us section on our website. Our Customer Service Team are available 9am - 5pm weekdays and 9am - 3pm on Saturdays. Please allow 24 hours for us to respond to enquiries.
        </>
      ),
    },
    {
      id: "tc-22",
      q: "Fraud Prevention",
      a: (
        <>
          <p className="ctct-body-text">
            To help prevent credit/debit card fraud, we may request additional information should we have any concerns with your order. This may take the form of billing address or simply a conversation to clarify some issues to address our concerns.
          </p>
          <p className="ctct-body-text">Under no circumstances will we ever request card details from you and these should never be provided under any circumstances.</p>
          <p className="ctct-body-text">
            Any details which are confirmed as fraudulent are passed to the UK police force in cooperation with Action Fraud (http://www.actionfraud.police.uk). Credit card fraud is illegal and we will cooperate with all domestic and international police/crime prevention agencies and seek to prosecute all perpetrators to the fullest extent of the law.
          </p>
        </>
      ),
    },
    {
      id: "tc-23",
      q: "Disclaimer",
      a: "This website and its contents are provided without any representations or warranties of any kind, either express or implied. We disclaim all representations and warranties, including by way of example but not limitation, as to fitness for a particular purpose, to the fullest extent permitted by applicable laws. In addition, we do not represent or warrant that the information and/or facilities accessible via this website are accurate, complete or current, or that this website will be free of defects, including, but not limited to, viruses or other harmful elements. The user of this website assumes all costs arising as a result of the use of this website.",
    },
    {
      id: "tc-24",
      q: "Governing Law",
      a: "These Terms and Conditions and any contracts made under them are governed by and shall be governed and construed in accordance with the laws of Scotland whose courts shall be courts of exclusive competent jurisdiction. We make no representation that materials on this Site are appropriate or available for use in other locations, and accessing them from territories where their contents are illegal is prohibited. Customers who access this website from locations outside the United Kingdom do so at their own risk and on their own initiative and are responsible for compliance with local laws, to the extent that any local laws are applicable. Nothing in these Terms shall in any way be deemed to restrict or affect your statutory rights under Scottish law.",
    },
    {
      id: "tc-25",
      q: "Force Majeure",
      a: "UKSoccershop shall not be liable for any failure to perform its obligations where such failure is the result of acts of Nature (including fire, flood, earthquake).",
    },
  ]

  const storeInfoItems: AccordionItem[] = [
    {
      id: "si-1",
      q: "Where are you based?",
      a: "UKSoccershop are a UK based company located near Glasgow, Scotland. All our items are shipped from within the UK, but we do offer cheap, fast and efficient international shipping to every country in the world.",
    },
    {
      id: "si-2",
      q: "Where do you ship?",
      a: "Literally everywhere! We ship to every country in the world.",
    },
    {
      id: "si-3",
      q: "When did you open?",
      a: (
        <>
          <p className="ctct-body-text">
            UKSoccershop was founded in August 2004, making us one of the oldest online football stores. We have grown into one of the world&rsquo;s biggest football retailers and have shipped over 1 million orders.
          </p>
          <p className="ctct-body-text">
            We are so grateful for the amazing support our loyal and passionate customers have provided us and continually strive to increase and improve our product offerings and service levels.
          </p>
        </>
      ),
    },
  ]

  const corporateItems: AccordionItem[] = [
    {
      id: "co-1",
      q: "I would like to discuss a bulk order with you",
      a: (
        <>
          <p className="ctct-body-text">
            We are happy to discuss bulk orders and provide a discount where applicable. Please note the size of the discount will be dependent on the availability of stock and how quickly you require the order.
          </p>
          <p className="ctct-body-text">
            Please provide full details of your requirements, including the shirts you would like to order, the quantities you would be looking for, where you are based and whether this is a one off deal or a regular order.
          </p>
          <p className="ctct-body-text">
            To discuss your proposal or if you would like further information, please contact us and our team will respond to your enquiry within 24 hours.
          </p>
        </>
      ),
    },
    {
      id: "co-2",
      q: "I would like to discuss a marketing promotion",
      a: (
        <>
          <p className="ctct-body-text">
            We have run various promotions in the past for a number of leading marketing agencies and companies. Our range of football shirts are the perfect corporate gift or marketing promotion and with our unparalleled range of kits and customisation options we can certainly help you build that extra special promotion.
          </p>
          <p className="ctct-body-text">
            Please provide full details of the promotion you are looking to run, including shirts, quantities and timescales if possible and our team will get back to you with a proposal.
          </p>
          <p className="ctct-body-text">
            To discuss your proposal or if you would like further information, please contact us and our team will respond to your enquiry within 24 hours.
          </p>
        </>
      ),
    },
    {
      id: "co-3",
      q: "I would like to discuss a partnership proposal",
      a: (
        <>
          <p className="ctct-body-text">
            We have enjoyed many successful partnerships in the past and are always open minded to working with new partners. Whether you want to introduce your product range to our store, advertise through our various marketing channels or build a mutually beneficial partnership, we will be happy to hear your ideas.
          </p>
          <p className="ctct-body-text">
            Please provide full details of the partnership you are proposing and our team will get back to you with our feedback.
          </p>
          <p className="ctct-body-text">
            To discuss your proposal or if you would like further information, please contact us and our team will respond to your enquiry within 24 hours.
          </p>
        </>
      ),
    },
  ]

  const privacyItems: AccordionItem[] = [
    {
      id: "pv-1",
      q: "What personal information do we collect from the people that visit our blog, website or app?",
      a: (
        <>
          <p className="ctct-body-text">
            When ordering from UKSOCCERSHOP you (the customer) will be asked to provide us with certain information; this includes your name, address, email address and telephone number etc. This information then allows us to fulfil your purchases / orders in good faith.
          </p>
          <p className="ctct-body-text">
            This notice applies to all information collected or submitted on the UKSOCCERSHOP website. On some pages, you can purchase goods, make requests, and register to receive materials.
          </p>
          <p className="ctct-body-text">The types of personal information collected at these pages are:</p>
          <ul className="ctct-list">
            <li>Name</li>
            <li>Address</li>
            <li>Email address</li>
            <li>Credit Card details</li>
            <li>Phone number</li>
          </ul>
        </>
      ),
    },
    {
      id: "pv-2",
      q: "When do we collect information?",
      a: (
        <>
          <p className="ctct-body-text">
            We collect information from you when you register on our site, place an order, subscribe to a newsletter, fill out a form or enter information on our site.
          </p>
          <p className="ctct-body-text">Provide us with feedback on our products or services</p>
        </>
      ),
    },
    {
      id: "pv-3",
      q: "How do we use your information?",
      a: "We use the information you provide about yourself when purchasing / placing an order to complete that purchase / order. We do not share this information with outside parties except to the extent necessary to complete that specific purchase / order. UKSOCCERSHOP does not sell, rent or exchange your personal details with any third party. We use return email addresses to answer the email we receive. In addition, we may email you with news on our latest products and services but your email address will not be shared with outside parties. By purchasing from UKSOCCERSHOP we may use the information you provide to send you personalised communications on products and services that may be of interest to you. You can opt out of these emails at the point of purchase or by unsubscribing from emails. Finally, we never use or share the personally identifiable information provided to us online in ways unrelated to the ones described above without also providing you an opportunity to opt-out or otherwise prohibit such unrelated uses. Should you never wish to receive further updates from us, please email us at info@uksoccershop.com or log in to your account on the website.",
    },
    {
      id: "pv-4",
      q: "How do we protect your information?",
      a: (
        <>
          <p className="ctct-body-text">
            Our website is scanned on a regular basis for security holes and known vulnerabilities in order to make your visit to our site as safe as possible. We use regular Malware Scanning.
          </p>
          <p className="ctct-body-text">
            Your personal information is contained behind secured networks and is only accessible by a limited number of persons who have special access rights to such systems, and are required to keep the information confidential. In addition, all sensitive/credit information you supply is encrypted via Secure Socket Layer (SSL) technology.
          </p>
          <p className="ctct-body-text">
            We implement a variety of security measures when a user places an order enters, submits, or accesses their information to maintain the safety of your personal information.
          </p>
          <p className="ctct-body-text">
            All transactions are processed through a gateway provider and are not stored or processed on our servers.
          </p>
        </>
      ),
    },
    {
      id: "pv-5",
      q: "Do we use 'cookies'?",
      a: (
        <>
          <p className="ctct-body-text">
            Yes. Cookies are small files that a site or its service provider transfers to your computer&rsquo;s hard drive through your Web browser (if you allow) that enables the site&rsquo;s or service provider&rsquo;s systems to recognize your browser and capture and remember certain information. For instance, we use cookies to help us remember and process the items in your shopping cart. They are also used to help us understand your preferences based on previous or current site activity, which enables us to provide you with improved services. We also use cookies to help us compile aggregate data about site traffic and site interaction so that we can offer better site experiences and tools in the future.
          </p>
          <p className="ctct-subheading">We use cookies to:</p>
          <ul className="ctct-list">
            <li>Help remember and process the items in the shopping cart.</li>
            <li>Understand and save user&rsquo;s preferences for future visits.</li>
            <li>Keep track of advertisements.</li>
            <li>Compile aggregate data about site traffic and site interactions in order to offer better site experiences and tools in the future. We may also use trusted third-party services that track this information on our behalf.</li>
          </ul>
          <p className="ctct-body-text">
            You can choose to have your computer warn you each time a cookie is being sent, or you can choose to turn off all cookies. You do this through your browser settings. Since browser is a little different, look at your browser&rsquo;s Help Menu to learn the correct way to modify your cookies.
          </p>
        </>
      ),
    },
    {
      id: "pv-6",
      q: "If users disable cookies in their browser",
      a: (
        <>
          <p className="ctct-body-text">If you turn cookies off it will turn off some of the features of the site.</p>
          <p className="ctct-body-text">For more information about cookie click here.</p>
        </>
      ),
    },
    {
      id: "pv-7",
      q: "Third-party disclosure",
      a: (
        <>
          <p className="ctct-subheading">Third-party links</p>
          <p className="ctct-body-text">
            Occasionally, at our discretion, we may include or offer third-party products or services on our website. These third-party sites have separate and independent privacy policies. We therefore have no responsibility or liability for the content and activities of these linked sites. Nonetheless, we seek to protect the integrity of our site and welcome any feedback about these sites.
          </p>
          <p className="ctct-subheading">Google</p>
          <p className="ctct-body-text">
            Google&rsquo;s advertising requirements can be summed up by Google&rsquo;s Advertising Principles. They are put in place to provide a positive experience for users.{" "}
            <a href="https://support.google.com/adwordspolicy/answer/1316548?hl=en" target="_blank" rel="noopener noreferrer" className="ctct-inline-link">
              https://support.google.com/adwordspolicy/answer/1316548?hl=en
            </a>
          </p>
          <p className="ctct-body-text">We use Google AdSense Advertising on our website.</p>
          <p className="ctct-body-text">
            Google, as a third-party vendor, uses cookies to serve ads on our site. Google&rsquo;s use of the DART cookie enables it to serve ads to our users based on previous visits to our site and other sites on the Internet. Users may opt-out of the use of the DART cookie by visiting the Google Ad and Content Network privacy policy.
          </p>
          <p className="ctct-subheading">Rakuten Advertising</p>
          <p className="ctct-body-text">
            Rakuten Advertising may collect personal information when you interact with our digital property, including IP addresses, digital identifiers, information about your web browsing and app usage and how you interact with our properties and ads for a variety of purposes, such as personalization of offers or advertisements, analytics about how you engage with websites or ads and other commercial purposes. For more information about the collection, use and sale of your personal data and your rights, please use the below links.
          </p>
          <p className="ctct-body-text">
            Privacy Policy:{" "}
            <a href="https://rakutenadvertising.com/legal-notices/services-privacy-policy/" target="_blank" rel="noopener noreferrer" className="ctct-inline-link">
              https://rakutenadvertising.com/legal-notices/services-privacy-policy/
            </a>
          </p>
          <p className="ctct-body-text">
            Your Rights/Opt Out:{" "}
            <a href="https://rakutenadvertising.com/legal-notices/services-privacy-rights-request-form/" target="_blank" rel="noopener noreferrer" className="ctct-inline-link">
              https://rakutenadvertising.com/legal-notices/services-privacy-rights-request-form/
            </a>
          </p>
        </>
      ),
    },
  ]

  const visibleContactFaqs =
    selectedSubject === "Please Select"
      ? faqItems
      : faqItems.filter((f: any) => f.subjects.includes(selectedSubject))

  const renderContent = () => {
    switch (activeSection) {
      case "faq":
        return (
          <>
            <h1 className="ctct-title">Top 5 Frequently Asked Questions</h1>
            <p className="ctct-subheading">New Orders</p>
            <Accordion items={newOrderFaqItems} openItems={openItems} onToggle={toggleItem} />
            <p className="ctct-subheading">Existing Orders</p>
            <Accordion items={existingOrderFaqItems} openItems={openItems} onToggle={toggleItem} />
          </>
        )

      case "trackOrder":
        return (
          <>
            <h1 className="ctct-title">WHERE IS MY ORDER?</h1>
            <Accordion items={trackOrderItems} openItems={openItems} onToggle={toggleItem} />
          </>
        )

      case "placeOrder":
        return (
          <>
            <h1 className="ctct-title">Placing Your Order</h1>
            <Accordion items={placeOrderItems} openItems={openItems} onToggle={toggleItem} />
          </>
        )

      case "shipping":
        return (
          <>
            <h1 className="ctct-title">Delivery &amp; Shipping</h1>
            <Accordion items={shippingItems} openItems={openItems} onToggle={toggleItem} />
          </>
        )

      case "returns":
        return (
          <>
            <h1 className="ctct-title">Return &amp; Exchange Policy</h1>

            <p className="ctct-subheading">Return Window</p>
            <ul className="ctct-list">
              <li>You must notify us of your intent to return within 14 days of delivery</li>
              <li>All returns must be completed within 28 days of delivery</li>
              <li>Contact us via email or phone to initiate your return</li>
            </ul>

            <p className="ctct-subheading">Standard Returns</p>
            <ul className="ctct-list">
              <li>Most items can be returned for a refund or exchange if you&rsquo;re not completely satisfied</li>
              <li>Items must be in original, unused condition with all tags and packaging intact</li>
              <li>Include the return form from your account with your package</li>
              <li>Note on the invoice whether you want a refund or exchange</li>
            </ul>

            <p className="ctct-subheading">Exceptions</p>
            <ul className="ctct-list">
              <li>Printed shirts and personalized items cannot be returned unless faulty</li>
              <li>Items with player names/numbers cannot be returned if the player leaves the club or changes their number</li>
            </ul>

            <p className="ctct-subheading">Return Process</p>
            <ul className="ctct-list">
              <li>Access the return form through your account</li>
              <li>Pack items securely with all original packaging and tags</li>
              <li>Include completed return form and invoice noting reason for return</li>
              <li>
                Ship to: UKSS, 18 Kilbirnie Place, Glasgow, G5 8QR, UNITED KINGDOM
              </li>
            </ul>

            <p className="ctct-subheading">Shipping &amp; Costs</p>
            <ul className="ctct-list">
              <li>We recommend using an insured, trackable shipping service</li>
              <li>For non-faulty returns, customer is responsible for return shipping costs</li>
              <li>For exchanges, customer pays shipping on replacement item per our shipping rate chart</li>
              <li>Items remain customer property until safely delivered to our return address</li>
            </ul>

            <p className="ctct-subheading">Refunds</p>
            <ul className="ctct-list">
              <li>Credit card purchases will be refunded to the original card</li>
              <li>Check or money order purchases are eligible for store credit or exchange only</li>
            </ul>

            <p className="ctct-subheading">Important Notes</p>
            <ul className="ctct-list">
              <li>Returns will be inspected upon receipt</li>
              <li>If exchange item is unavailable, a refund will be issued</li>
              <li>We are not responsible for items lost or damaged during return shipping</li>
            </ul>
          </>
        )

      case "personalisation":
        return (
          <>
            <h1 className="ctct-title">Personalisation</h1>
            <Accordion items={personalisationItems} openItems={openItems} onToggle={toggleItem} />
          </>
        )

      case "orderQueries":
        return (
          <>
            <h1 className="ctct-title">Order Queries</h1>
            <Accordion items={orderQueryItems} openItems={openItems} onToggle={toggleItem} />
          </>
        )

      case "storeInfo":
        return (
          <>
            <h1 className="ctct-title">Store Information</h1>
            <Accordion items={storeInfoItems} openItems={openItems} onToggle={toggleItem} />
          </>
        )

      case "corporate":
        return (
          <>
            <h1 className="ctct-title">Corporate Enquiries</h1>
            <Accordion items={corporateItems} openItems={openItems} onToggle={toggleItem} />
          </>
        )

      case "technical":
        return (
          <>
            <h1 className="ctct-title">Technical Issues</h1>
            <Accordion items={technicalItems} openItems={openItems} onToggle={toggleItem} />
          </>
        )

      case "privacy":
        return (
          <>
            <h1 className="ctct-title">Privacy Notice</h1>
            <p className="ctct-body-text">
              This privacy policy has been compiled to better serve those who are concerned with how their &lsquo;Personally Identifiable Information&rsquo; (PII) is being used online. PII, as described in US privacy law and information security, is information that can be used on its own or with other information to identify, contact, or locate a single person, or to identify an individual in context. Please read our privacy policy carefully to get a clear understanding of how we collect, use, protect or otherwise handle your Personally Identifiable Information in accordance with our website.
            </p>
            <Accordion items={privacyItems} openItems={openItems} onToggle={toggleItem} />
          </>
        )

      case "cookie":
        return (
          <>
            <h1 className="ctct-title">Cookie Usage</h1>
            <p className="ctct-body-text">
              This is the Cookie Policy for Uksoccershop, accessible from https://www.uksoccershop.com
            </p>
            <Accordion items={cookieItems} openItems={openItems} onToggle={toggleItem} />
          </>
        )

      case "terms":
        return (
          <>
            <h1 className="ctct-title">Terms &amp; Conditions</h1>
            <p className="ctct-body-text ctct-notice">PLEASE READ THESE TERMS OF USE CAREFULLY BEFORE USING THIS WEBSITE</p>
            <p className="ctct-body-text">
              This page explains how you may use this website and contains the legal terms and conditions on which we sell products to you. On this page, the words &ldquo;we&rdquo;, &ldquo;us&rdquo; and &ldquo;our&rdquo; refer to UKSoccershop. By using this website or any sub-site accessible on this website, the user (&ldquo;user&rdquo; or &ldquo;you&rdquo;) accepts without limitation of qualification, the Terms of Use, whether or not you are a registered member of UKSoccershop.
            </p>
            <p className="ctct-body-text">
              It is important you read this page carefully, as it forms part of the contract between us. Our relationship is subject to these Terms, which impose certain responsibilities upon you and explains the exclusions and limitations of our liability to you in the event of loss or damage.
            </p>
            <p className="ctct-body-text">
              We may change these Terms of Use at any time without notice and you should review this page regularly to make yourself aware of any changes. Your continued use of the Website shall be considered your acceptance to the revised Terms of Use. If you do not agree to these Terms and Conditions, you should not use this website.
            </p>
            <p className="ctct-body-text">You should print and keep a copy of these Terms for future reference.</p>
            <Accordion items={termsItems} openItems={openItems} onToggle={toggleItem} />
          </>
        )

      case "contact":
      default:
        return (
          <>
            <h1 className="ctct-title">Contact Us</h1>
            <p className="ctct-body-text">
              If you have a query about your order you have placed, or are looking to place, with us,
              you can either contact us using the form or via phone on <span>0333 210 0268</span>
            </p>
            <p className="ctct-body-text ctct-notice">
              Please note that due to reduced staff levels and high volumes, our phone service is
              currently not operational and we are only able to offer email support at this time.
            </p>
            <p className="ctct-body-text">Our customer service opening hours are:</p>
            <HoursTable />
            <p className="ctct-inquire-label">
              IF YOU WANT TO INQUIRE ABOUT ANYTHING PLEASE FEEL FREE TO CONTACT US AT
            </p>
            <div className="ctct-address-block">
              <p className="ctct-addr-bordered"><strong>UKSoccershop</strong></p>
              <p className="ctct-addr-bordered">18 Kilbirnie Place,</p>
              <p className="ctct-address-line">Glasgow, G5 8QR,</p>
              <p className="ctct-address-line">UNITED KINGDOM</p>
              <strong className="ctct-address-email">
                Email:{" "}
                <a href="mailto:info@uksoccershop.com" className="ctct-email-link">
                  info@uksoccershop.com
                </a>
              </strong>
            </div>
            <div className="ctct-help-form" id="helpForm">
              <h5 className="ctct-help-heading">What do you need help with?</h5>
              <div className="ctct-form-group">
                <label className="ctct-form-label" htmlFor="contact-subject">Subject*</label>
                <select
                  id="contact-subject"
                  className="ctct-form-select"
                  value={selectedSubject}
                  onChange={(e) => setSelectedSubject(e.target.value)}
                >
                  {subjectOptions.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
            </div>
            <form onSubmit={handleContactSubmit} className="ctct-contact-form" id="query">
              <div className="ctct-form-group">
                <label className="ctct-form-label" htmlFor="contact-order-no">Order No. (optional):</label>
                <input
                  id="contact-order-no"
                  type="text"
                  value={orderNo}
                  onChange={(e) => setOrderNo(e.target.value)}
                  className="ctct-form-input"
                />
              </div>
              <div className="ctct-form-group">
                <label className="ctct-form-label" htmlFor="contact-name">Full Name *</label>
                <input
                  id="contact-name"
                  type="text"
                  required
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  className="ctct-form-input"
                />
              </div>
              <div className="ctct-form-group">
                <label className="ctct-form-label" htmlFor="contact-email">Email Address *</label>
                <input
                  id="contact-email"
                  type="email"
                  required
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  className="ctct-form-input"
                />
              </div>
              <div className="ctct-form-group">
                <label className="ctct-form-label" htmlFor="contact-phone">Phone</label>
                <input
                  id="contact-phone"
                  type="text"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  className="ctct-form-input"
                />
              </div>
              <div className="ctct-form-group">
                <label className="ctct-form-label" htmlFor="contact-message">Your Message Here *</label>
                <textarea
                  id="contact-message"
                  required
                  rows={4}
                  value={contactMessage}
                  onChange={(e) => setContactMessage(e.target.value)}
                  className="ctct-form-input"
                />
              </div>
              <input type="hidden" name="subject" value={selectedSubject} />
              <ReCAPTCHA
                ref={contactRecaptchaRef}
                sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY as string}
                onChange={(token) => {
                  setContactRecaptchaToken(token)
                  setContactError(null)
                }}
                onExpired={() => setContactRecaptchaToken(null)}
              />
              {contactError && <p className="ctct-form-error">{contactError}</p>}
              {contactSubmitted && (
                <p className="ctct-form-success">Thanks — your message has been received.</p>
              )}
              <button type="submit" className="ctct-cta-btn" style={{ marginTop: "12px" }}>Submit</button>
            </form>
            <div className="ctct-quick-wrap">
              <div className="ctct-quick-answer">
                <h5 className="ctct-quick-header">
                  Need a quick answer? <span className="ctct-chevron-down">&#9662;</span>
                </h5>
                <div className="ctct-quick-tabs">
                  {visibleContactFaqs.length === 0 ? (
                    <p className="ctct-faq-empty">No FAQs for this topic — please contact us directly.</p>
                  ) : (
                    visibleContactFaqs.map((faq: any) => (
                      <div key={faq.id} className="ctct-faq-item">
                        <h4
                          className="ctct-faq-q"
                          onClick={() => toggleItem(faq.id)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => e.key === "Enter" && toggleItem(faq.id)}
                          aria-expanded={openItems.has(faq.id)}
                        >
                          {faq.q}
                          <span className="ctct-faq-plus">{openItems.has(faq.id) ? "−" : "+"}</span>
                        </h4>
                        {openItems.has(faq.id) && (
                          <div className="ctct-faq-answer">
                            <div className="ctct-body-text">{faq.a}</div>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
            <p className="ctct-cta-p">
              <a href="mailto:info@uksoccershop.com" className="ctct-cta-btn">
                Still need to contact us?
              </a>
            </p>
          </>
        )
    }
  }

  return (
    <div className="ctct-page-wrap">
      <div className="ctct-container">

        {/* ── Left sidebar ─────────────────────────── */}
        <aside className="ctct-sidebar">
          <ul className="ctct-sidebar-list">
            {sidebarSections.map((section) => (
              <li
                key={section.id}
                className={`ctct-sidebar-item${activeSection === section.id ? " ctct-sidebar-item-active" : ""}`}
              >
                <button
                  type="button"
                  className="ctct-sidebar-link"
                  onClick={() => setActiveSection(section.id)}
                  aria-current={activeSection === section.id ? "page" : undefined}
                >
                  <span className="ctct-sidebar-icon">
                    <IconFa d={section.d} />
                  </span>
                  <span className="ctct-sidebar-label">{section.label}</span>
                  <span className="ctct-sidebar-arrow">
                    {activeSection === section.id ? "−" : "+"}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </aside>

        {/* ── Main content ──────────────────────────── */}
        <main className="ctct-main">
          {renderContent()}
        </main>

      </div>
    </div>
  )
}

export default ContactTemplate
